import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

/**
 * BACKGROUND AUDIO COMPONENT
 * 
 * - Standard HTML5 Audio Tag for reliability.
 * - Test URL fallback to prove it works before user replaces broken file.
 * - Mute/Unmute toggle.
 * - Stops playing when user scrolls past Hero section.
 */
const BackgroundAudio = () => {
  const [isMuted, setIsMuted] = useState(true);
  const audioRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [useTestUrl, setUseTestUrl] = useState(false);

  // Initialize and handle playback
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.4; // Pleasant background volume
      audioRef.current.muted = true;
    }
  }, []);

  const handleScroll = useCallback(() => {
    const scrollPos = window.scrollY;
    const threshold = window.innerHeight * 0.8;
    
    if (scrollPos > threshold) {
      if (isVisible) {
        setIsVisible(false);
        if (audioRef.current) audioRef.current.pause();
      }
    } else {
      if (!isVisible) {
        setIsVisible(true);
        if (audioRef.current && !isMuted) audioRef.current.play().catch(() => {});
      }
    }
  }, [isVisible, isMuted]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.muted = false;
        audioRef.current.play().then(() => {
          setIsMuted(false);
        }).catch(() => {
           console.log("Interaction required for audio");
        });
      } else {
        audioRef.current.muted = true;
        audioRef.current.pause();
        setIsMuted(true);
      }
    }
  };

  const handleAudioError = () => {
    console.error("Local audio file is broken (9-bytes, 'Not Found'). Switching to Test URL...");
    setUseTestUrl(true);
  };

  return (
    <>
      {/* HTML5 Audio Player - Uses local file, falls back to test URL if local is broken */}
      <audio
        ref={audioRef}
        src={useTestUrl ? "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" : "/audio/background-music.mp3"}
        loop
        preload="auto"
        onError={handleAudioError}
      />

      {/* FIXED VISIBLE BUTTON: Gold/Maroon theme to match Taalsetu style */}
      <div 
        className={`fixed bottom-12 right-12 z-[9999] transition-all duration-700 transform ${
          isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-24 opacity-0 scale-50'
        }`}
      >
        <button
          onClick={toggleMute}
          className={`flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 active:scale-95 ${
            isMuted 
              ? 'bg-maroon-dark text-gold border-2 border-gold/40' 
              : 'bg-gold text-maroon-dark border-2 border-gold shadow-gold/30'
          }`}
          title={isMuted ? "Play Background Music" : "Mute Background Music"}
        >
           {/* Pulsing ring when unmuted */}
           {!isMuted && (
            <span className="absolute inset-0 rounded-full bg-gold/50 animate-ping"></span>
          )}

          {isMuted ? (
            <FaVolumeMute size={28} />
          ) : (
            <FaVolumeUp size={28} />
          )}

          {/* Status Message for Debugging (Only shows if useTestUrl) */}
          {useTestUrl && isMuted && (
            <div className="absolute -top-10 right-0 left-0 text-center whitespace-nowrap">
               <span className="bg-red-500 text-white text-[10px] px-2 py-1 rounded-md">Local file broken! Using Test URL</span>
            </div>
          )}
          
          {/* Animated Music Bars when playing */}
          {!isMuted && isVisible && (
            <div className="absolute -top-12 flex items-end space-x-1 h-8">
              <span className="w-1 bg-gold rounded-full animate-music-bar-1" style={{minHeight:'4px'}}></span>
              <span className="w-1 bg-gold rounded-full animate-music-bar-2" style={{minHeight:'8px'}}></span>
              <span className="w-1 bg-gold rounded-full animate-music-bar-3" style={{minHeight:'6px'}}></span>
              <span className="w-1 bg-gold rounded-full animate-music-bar-4" style={{minHeight:'3px'}}></span>
            </div>
          )}
        </button>
      </div>
    </>
  );
};

export default BackgroundAudio;
